import { useState } from "react";
import { eventConsultingSchema, EVENT_FORMATS, eventFormatLabels, type EventConsultingPayload } from "@anton/shared";
import { PageHeader } from "../components/PageHeader";
import { Field } from "../components/Field";
import { RadioGroup } from "../components/RadioGroup";
import { SubmitButton } from "../components/SubmitButton";
import { SuccessScreen } from "../components/SuccessScreen";
import { Card, Lead, P, H3, CheckList, Stat, SectionHeader } from "../components/Section";
import { submitApplication } from "../lib/api";
import { haptic, showAlert } from "../lib/telegram";

export default function EventConsulting() {
  const [form, setForm] = useState<Partial<EventConsultingPayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) return <SuccessScreen message="Заявка пришла мне лично. Свяжусь или подключу команду." />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = eventConsultingSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const i of parsed.error.issues) fe[i.path.join(".")] = i.message;
      setErrors(fe);
      haptic("error");
      return;
    }
    setLoading(true);
    const res = await submitApplication("EVENT_CONSULTING", parsed.data);
    setLoading(false);
    if (!res.ok) { showAlert(res.error ?? "Ошибка"); haptic("error"); return; }
    setDone(true);
  }

  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="Консалтинг по мероприятиям"
        title="Запуск масштабных форумов"
        subtitle="Помогу поднять выручку от билетов и спонсорства, усилить спикерский состав, выстроить операционку."
        icon="🎤"
        iconColor="pink"
      />

      <div className="px-5 mt-3 space-y-3">
        <Card>
          <Lead>
            7 лет в индустрии. Делал форумы от 500 до 7 000 участников.
            Знаю, где утекают билеты, как договариваться со спонсорами и почему срываются спикеры.
          </Lead>
        </Card>

        <div className="grid grid-cols-3 gap-3">
          <Stat value="7K+" label="макс. участников" />
          <Stat value="5M+" label="просмотров в СМИ" />
          <Stat value="7 лет" label="в эвент-индустрии" />
        </div>

        <Card>
          <H3>С чем помогаю</H3>
          <CheckList items={[
            "Маркетинг и продажа билетов — где взять трафик, как считать юнит-экономику",
            "Спонсорские пакеты — как упаковать и продать без скидки в 50%",
            "Спикерский состав — открываю свою сеть контактов",
            "Операционная система мероприятия — рассадка, регистрация, день Х",
            "Масштабирование формата — как из 500 человек выйти на 5000",
          ]} />
        </Card>

        <Card>
          <H3>Кто были спикеры на моих форумах</H3>
          <P>
            Джордан Белфорт. Оскар Хартманн. Евгений Чичваркин. Олег Тиньков. Игорь Манн.
            Михаил Гребенюк. Ксения Собчак. Ирина Хакамада. Фёдор Овчинников. Дмитрий Карпачёв.
            Маргулан Сейсембаев. Саша Митрошина.
          </P>
        </Card>

        <Card>
          <H3>Форматы сотрудничества</H3>
          <P>
            Выбери ниже, как удобно:
          </P>
          <div className="mt-3 space-y-2">
            <div className="ios-callout-muted">
              <b>🚀 Сопровождение под запуск</b> — 8–16 недель подготовки + запуск форума под ключ.
            </div>
            <div className="ios-callout-muted">
              <b>🤝 Партнёрство на серию мероприятий</b> — долгосрочная работа, прохожу с тобой несколько событий.
            </div>
            <div className="ios-callout-muted">
              <b>🗺 Разовая консультация</b> — дорожная карта улучшений и чек-лист. Без длинных обязательств.
            </div>
          </div>
        </Card>

        <div className="ios-callout-muted">
          Стоимость зависит от формата. Уточним на первом созвоне.
        </div>
      </div>

      <SectionHeader>Оставить заявку</SectionHeader>
      <form noValidate onSubmit={onSubmit} className="px-5 space-y-4">
        <Field label="Формат сотрудничества" error={errors["format"]}>
          <RadioGroup value={form.format}
            onChange={(v) => setForm({ ...form, format: v as EventConsultingPayload["format"] })}
            options={EVENT_FORMATS.map((v) => ({ value: v, label: eventFormatLabels[v] }))} />
        </Field>
        <Field label="Имя" error={errors["name"]}>
          <input className={`ios-input ${errors["name"] ? "ios-input-error" : ""}`}
            value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Контакт" error={errors["contact"]}>
          <input className={`ios-input ${errors["contact"] ? "ios-input-error" : ""}`}
            value={form.contact ?? ""} onChange={(e) => setForm({ ...form, contact: e.target.value })}
            placeholder="@username или телефон" />
        </Field>
        <Field label="Дата и масштаб мероприятия" error={errors["dateAndScale"]}>
          <input className={`ios-input ${errors["dateAndScale"] ? "ios-input-error" : ""}`}
            value={form.dateAndScale ?? ""}
            onChange={(e) => setForm({ ...form, dateAndScale: e.target.value })}
            placeholder="например: октябрь 2026, ~2000 участников" />
        </Field>
        <Field label="С чем нужна помощь?" error={errors["needHelpWith"]}>
          <textarea rows={4} className={`ios-input ${errors["needHelpWith"] ? "ios-input-error" : ""}`}
            value={form.needHelpWith ?? ""}
            onChange={(e) => setForm({ ...form, needHelpWith: e.target.value })}
            placeholder="спикеры / спонсоры / билеты / операционка / другое" />
        </Field>
        <div className="pt-2">
          <SubmitButton loading={loading}>Отправить заявку</SubmitButton>
        </div>
      </form>
    </div>
  );
}
